/* av-rewards-drawer.js — the "Team Scoreboard" card (#rewards) on the latest-templates archive page.
   Opens/closes the drawer that holds the team's Daily milestone, Release activity chain and Release
   History Achievements panels, and mirrors three summary chips from those panels' own DOM (it never
   recomputes a number). Plain ES5, no dependencies; loaded defer after av-release-feed.js; every query
   is optional, so it no-ops when the markup is absent. */
(function () {
  'use strict';
  var doc = document;
  var section = doc.querySelector('[data-role="team-board"]');
  if (!section) return;
  var head = section.querySelector('.av-board__head');
  var toggle = section.querySelector('[data-role="team-board-toggle"]');
  var label = section.querySelector('[data-role="team-board-label"]');
  var body = section.querySelector('[data-role="team-board-body"]');
  var closeBtn = section.querySelector('[data-role="team-board-close"]');
  var chipToday = section.querySelector('[data-role="team-board-chip-today"]');
  var chipStreak = section.querySelector('[data-role="team-board-chip-streak"]');
  var chipMedals = section.querySelector('[data-role="team-board-chip-medals"]');
  if (!toggle || !body) return;

  var KEY = 'avTeamBoardOpen';
  var HASH = '#rewards';
  var OPEN_LABEL = 'Open scoreboard';
  var CLOSE_LABEL = 'Close scoreboard';

  function reducedMotion() {
    try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; }
  }
  function remember(open) {
    try { if (open) sessionStorage.setItem(KEY, '1'); else sessionStorage.removeItem(KEY); } catch (e) {}
  }
  function remembered() {
    try { return sessionStorage.getItem(KEY) === '1'; } catch (e) { return false; }
  }
  function setIcon(glyph) {
    var icon = toggle.querySelector('.av-board__toggle-icon');
    if (icon) icon.textContent = glyph;
  }
  function isOpen() { return !body.hidden; }

  // the daily bar's width transition never ran while display:none — refill it from 0 as on first render
  function retriggerBars() {
    var bars = body.querySelectorAll('[data-role="creator-progress-bar"]');
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      var w = bar.style.width;
      if (!w) continue;
      bar.style.transition = 'none';
      bar.style.width = '0%';
      void bar.offsetWidth;
      bar.style.transition = '';
      bar.style.width = w;
    }
  }

  // the open drawer shifts every date group: re-measure via the build's Load-more hook (never a fake resize)
  function afterToggle(open) {
    requestAnimationFrame(function () {
      if (typeof window.avReleaseDecorate === 'function') { try { window.avReleaseDecorate(); } catch (e) {} }
    });
    try { doc.dispatchEvent(new CustomEvent('av:board-toggle', { detail: { open: open } })); } catch (e) {}
  }

  function open(silent) {
    if (isOpen()) return;
    body.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    if (label) label.textContent = CLOSE_LABEL;
    setIcon('−');
    section.classList.add('is-open');
    if (!silent && !reducedMotion()) {
      body.classList.remove('is-in');
      void body.offsetWidth;
      body.classList.add('is-in');
      retriggerBars();
    }
    remember(true);
    afterToggle(true);
  }

  function close(returnFocus) {
    if (!isOpen()) return;
    body.hidden = true;
    body.classList.remove('is-in');
    section.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    if (label) label.textContent = OPEN_LABEL;
    setIcon('+');
    if (returnFocus) {
      try { toggle.focus(); } catch (e) {}
      try { (head || section).scrollIntoView({ block: 'nearest' }); } catch (e) {}
    }
    remember(false);
    if (location.hash === HASH) {
      try { history.replaceState(null, '', location.pathname + location.search); } catch (e) {}
    }
    afterToggle(false);
  }

  toggle.addEventListener('click', function () { if (isOpen()) close(false); else open(false); });
  if (closeBtn) closeBtn.addEventListener('click', function () { close(true); });
  body.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); close(true); }
  });

  function openFromHash() {
    if (location.hash !== HASH) return;
    open(false);
    requestAnimationFrame(function () {
      try { section.scrollIntoView({ block: 'start' }); } catch (e) {}
    });
  }
  window.addEventListener('hashchange', openFromHash);

  // ---------- summary chips: mirrored from the panels' DOM ----------
  var TIERS = ['bronze', 'silver', 'gold', 'platinum'];
  function feedReady() { return !!(window.avReleaseFeed && window.avReleaseFeed.ready); }
  function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }
  function num(el) {
    if (!el) return 0;
    var n = parseInt((el.textContent || '').replace(/[^\d]/g, ''), 10);
    return isFinite(n) ? n : 0;
  }
  function plural(n, word) { return n + ' ' + word + (n === 1 ? '' : 's'); }

  function mirrorToday() {
    if (!chipToday) return;
    var li = chipToday.parentNode;
    var panel = doc.querySelector('[data-role="creator-panel"]');
    if (!panel) { if (li) li.hidden = true; return; }
    if (panel.hasAttribute('data-av-pending')) {
      chipToday.textContent = 'Today: counting…';
      if (li) li.setAttribute('data-tier', 'pending');
      return;
    }
    var count = parseInt(panel.getAttribute('data-today-count') || '0', 10);
    if (!isFinite(count)) count = 0;
    var tier = 'idle';
    for (var i = 0; i < TIERS.length; i++) if (panel.classList.contains('is-' + TIERS[i])) tier = TIERS[i];
    chipToday.textContent = tier === 'idle'
      ? 'Today: ' + plural(count, 'release')
      : 'Today: ' + cap(tier) + ' · ' + plural(count, 'release');
    if (li) li.setAttribute('data-tier', tier);
  }

  function mirrorStreak() {
    if (!chipStreak) return;
    if (!feedReady()) { chipStreak.textContent = 'Streak: …'; return; }
    var streak = num(doc.querySelector('[data-role="streak-count"]'));
    var text = 'Streak: ' + plural(streak, 'day');
    var powerEl = doc.querySelector('[data-role="streak-power"]');
    var m = powerEl ? /×\s*(\d+)/.exec(powerEl.textContent || '') : null;
    if (m && parseInt(m[1], 10) > 1) text += ' · ×' + m[1];
    chipStreak.textContent = text;
  }

  function mirrorMedals() {
    if (!chipMedals) return;
    if (!feedReady()) { chipMedals.textContent = 'Medals: …'; return; }
    var parts = [];
    for (var i = 0; i < TIERS.length; i++) {
      var n = num(doc.querySelector('[data-role="achievement-' + TIERS[i] + '"]'));
      if (n > 0) parts.push({ tier: TIERS[i], n: n });
    }
    while (chipMedals.firstChild) chipMedals.removeChild(chipMedals.firstChild);
    if (!parts.length) { chipMedals.textContent = 'Medals: none yet'; return; }
    chipMedals.appendChild(doc.createTextNode('Medals: '));
    for (var j = 0; j < parts.length; j++) {
      if (j) chipMedals.appendChild(doc.createTextNode(' · '));
      var src = doc.querySelector('.latest-achievement-card[data-tier="' + parts[j].tier + '"] img');
      if (src && src.getAttribute('src')) {
        var img = doc.createElement('img');
        img.src = src.getAttribute('src');
        img.alt = cap(parts[j].tier);
        img.width = 14;
        img.height = 14;
        chipMedals.appendChild(img);
        chipMedals.appendChild(doc.createTextNode(' '));
      } else {
        chipMedals.appendChild(doc.createTextNode(cap(parts[j].tier) + ' '));
      }
      chipMedals.appendChild(doc.createTextNode(String(parts[j].n)));
    }
  }

  function mirror() { mirrorToday(); mirrorStreak(); mirrorMedals(); }

  var queued = false;
  function queueMirror() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; mirror(); });
  }
  doc.addEventListener('av:release-ready', queueMirror);
  if (window.MutationObserver) {
    new MutationObserver(queueMirror).observe(body, {
      subtree: true, childList: true, characterData: true,
      attributes: true, attributeFilter: ['class', 'data-today-count', 'data-av-pending']
    });
  }

  // ---------- init ----------
  mirror();
  if (location.hash === HASH) openFromHash();
  else if (remembered()) open(true);
})();
